"use client";

import { useState, useCallback, useMemo } from "react";
import {
  useCheckoutBase,
  defaultGuest,
  defaultDeliveryAddress,
} from "./use-checkout-base";
import { validateGuest, hasGuestErrors } from "../validation";
import type {
  CheckoutStep,
  Guest,
  DeliveryAddress,
  GiftingDetails,
} from "../utils/types";

export type UseCheckoutProductProps = {
  product: any;
  postages?: any[];
  user?: any;
  discount?: any;
  school?: {
    _id: string;
    name?: string;
    slug?: string;
  };
  onValidateDiscount?: (
    code: string,
    email: string,
  ) => Promise<{
    isValid: boolean;
    discountValue: number;
    percentage?: number;
    message?: string;
  }>;
};

export function useCheckoutProduct({
  product,
  postages = [],
  user,
  discount: initialDiscount,
  school,
  onValidateDiscount,
}: UseCheckoutProductProps) {
  const base = useCheckoutBase({
    user,
    enabledAddOns: [],
    isSendingMaterial: true,
  });

  // Product-specific state
  const [quantity, setQuantity] = useState(1);
  const [selectedPostageId, setSelectedPostageId] = useState<
    string | undefined
  >(postages[0]?._id);
  const [isGift, setIsGift] = useState(false);
  const [giftingDetails, setGiftingDetails] = useState<GiftingDetails>({
    to: "",
    from: "",
    message: "",
  });

  // Step management
  const [step, setStep] = useState<CheckoutStep>("product");

  // Selected postage
  const selectedPostage = useMemo(() => {
    return postages.find((p: any) => p._id === selectedPostageId);
  }, [postages, selectedPostageId]);

  // Calculate total
  const total = useMemo(() => {
    const productTotal = (product.price || 0) * quantity;
    // Postage is charged PER UNIT and the gift fee ONCE per order — the split
    // the server charges (ValidateBasketItems' resolveProductSurcharges) and
    // the one the legacy product checkout charged and displayed. Showing a
    // flat postage here would undercut the basket total by (qty − 1) × postage.
    const postageTotal = (selectedPostage?.price || 0) * quantity;
    const giftingFee = isGift ? product.gifting?.giftingFee || 0 : 0;
    const grossTotal = productTotal + postageTotal + giftingFee;

    // Calculate discount based on type
    const discountData = base.discount || initialDiscount;
    let discountAmount = 0;
    if (discountData) {
      const { value, type } = discountData;
      switch (type) {
        case "PERCENTAGE":
          discountAmount = Math.round(
            (grossTotal * Math.min(value, 100)) / 100,
          );
          break;
        case "PRICE":
        default:
          discountAmount = Math.min(grossTotal, value);
          break;
      }
    }

    return Math.max(0, grossTotal - discountAmount);
  }, [
    product,
    quantity,
    selectedPostage,
    isGift,
    base.discount,
    initialDiscount,
  ]);

  // Calculate the discount amount for display purposes
  const discountAmount = useMemo(() => {
    const discountData = base.discount || initialDiscount;
    if (!discountData) return 0;

    const productTotal = (product.price || 0) * quantity;
    // Per-unit postage, once-per-order gifting — see `total` above.
    const postageTotal = (selectedPostage?.price || 0) * quantity;
    const giftingFee = isGift ? product.gifting?.giftingFee || 0 : 0;
    const grossTotal = productTotal + postageTotal + giftingFee;

    const { value, type } = discountData;

    switch (type) {
      case "PERCENTAGE":
        return Math.round((grossTotal * Math.min(value, 100)) / 100);
      case "PRICE":
      default:
        return Math.min(grossTotal, value);
    }
  }, [product, quantity, selectedPostage, isGift, base.discount, initialDiscount]);

  const isFree = total === 0;

  // Visible steps
  const visibleSteps = useMemo(() => {
    const steps: CheckoutStep[] = ["product"];

    if (
      product.userQuestionForm?.isEnabled &&
      product.userQuestionForm?.questions?.length > 0
    ) {
      steps.push("userform");
    }

    steps.push("personal");

    steps.push("basketChoice");

    return steps;
  }, [product.userQuestionForm]);

  const currentStepIndex = visibleSteps.indexOf(step);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === visibleSteps.length - 1;

  const goToNextStep = useCallback(() => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < visibleSteps.length) {
      setStep(visibleSteps[nextIndex]!);
    }
  }, [currentStepIndex, visibleSteps]);

  const goToPreviousStep = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setStep(visibleSteps[prevIndex]!);
    }
  }, [currentStepIndex, visibleSteps]);

  const goToStep = useCallback(
    (targetStep: CheckoutStep) => {
      if (visibleSteps.includes(targetStep)) {
        setStep(targetStep);
      }
    },
    [visibleSteps],
  );

  // Update gifting details - accepts full GiftingDetails object
  const updateGiftingDetails = useCallback((details: GiftingDetails) => {
    setGiftingDetails(details);
  }, []);

  // Apply discount
  const applyDiscount = useCallback(
    async (code: string) => {
      if (!onValidateDiscount) return;

      const email = base.personalDetails.guests[0]?.email || "";
      base.setIsLoading(true);
      base.clearError();

      try {
        const result = await onValidateDiscount(code, email);
        // API returns discount object directly: { value, type, code, percentage, ... }
        // or null/undefined if invalid
        if (result && result.value !== undefined) {
          base.setDiscount({
            value: result.value,
            type: result.type,
            percentage: result.percentage,
          });
          base.setDiscountCode(result.code || code);
        } else {
          base.setDiscount(null);
          const errorMsg = result?.message || "Invalid discount code";
          base.setError(errorMsg);
          throw new Error(errorMsg);
        }
      } catch (error: any) {
        base.setDiscount(null);
        const errorMsg =
          error?.response?.data?.message ||
          (error instanceof Error ? error.message : null) ||
          "Failed to validate discount";
        base.setError(errorMsg);
        throw new Error(errorMsg);
      } finally {
        base.setIsLoading(false);
      }
    },
    [onValidateDiscount, base],
  );

  // Clear discount
  const clearDiscount = useCallback(() => {
    base.setDiscount(null);
    base.setDiscountCode("");
  }, [base]);

  // Validation
  const canContinue = useCallback((): boolean => {
    switch (step) {
      case "product": {
        const hasQuantityAndPostage = quantity > 0 && !!selectedPostageId;
        // If gifting is selected, validate gifting details
        if (isGift) {
          const hasGiftingDetails =
            giftingDetails.to.trim() !== "" &&
            giftingDetails.from.trim() !== "" &&
            giftingDetails.message.trim() !== "";
          return hasQuantityAndPostage && hasGiftingDetails;
        }
        return hasQuantityAndPostage;
      }
      case "userform":
        return base.isUserFormValid;
      case "personal": {
        const guests = base.personalDetails.guests;
        const guest = guests[0];
        const addr = base.personalDetails.deliveryAddress;
        if (!guest || !addr) return false;

        const hasValidGuests = guests
          .map((currentGuest, index) => {
            const errors = validateGuest(
              {
                firstName: currentGuest?.firstName || "",
                lastName: currentGuest?.lastName || "",
                email: currentGuest?.email || "",
                phoneNumber: currentGuest?.phoneNumber || "",
              },
              {
                isFirstGuest: index === 0,
                requirePhone: true,
              },
            );
            return !hasGuestErrors(errors);
          })
          .every(Boolean);

        const hasAddress =
          !!addr.addressLine1?.trim() &&
          !!addr.city?.trim() &&
          !!addr.postCode?.trim() &&
          !!addr.country?.trim();

        return hasValidGuests && hasAddress;
      }
      case "basketChoice":
        return true;
      default:
        return false;
    }
  }, [step, quantity, selectedPostageId, isGift, giftingDetails, base]);

  // Get booking data for API
  const getBookingData = useCallback(() => {
    const { personalDetails, userFormValues } = base;
    const [guest] = personalDetails.guests;
    const originalPostage = postages.find(
      (p: any) => p._id === selectedPostageId,
    );

    return {
      giftingDetails: {
        message: giftingDetails.message,
        to: giftingDetails.to,
        from: giftingDetails.from,
        giftingFeeUsed: isGift ? (product.gifting?.giftingFee ?? 0) : 0,
      },
      cancelURL: typeof window !== "undefined" ? window.location.href : "",
      bookingType: "product",
      isFree,
      user: {
        _id: user?._id || user?.id,
        profile: {
          firstName: guest?.firstName,
          lastName: guest?.lastName,
          phone: guest?.phoneNumber,
        },
        renderEmail: guest?.email,
      },
      deliveryAddress: personalDetails.deliveryAddress,
      product,
      school,
      quantity,
      postage: originalPostage,
      userInputs: userFormValues,
      discountCode: base.discountCode,
      credit: 0,
      isAGift: isGift,
      marketingOptIn: guest?.marketingOptIn,
      _pricingSummary: {
        grossTotal: total + discountAmount,
        netTotal: total,
        discountAmount,
        discountType: base.discount ? "discount" : undefined,
        currency: product.currency || "GBP",
      },
    };
  }, [
    base,
    product,
    school,
    quantity,
    postages,
    selectedPostageId,
    giftingDetails,
    isGift,
    isFree,
    user,
    total,
    discountAmount,
  ]);

  return {
    // State
    step,
    quantity,
    selectedPostageId,
    selectedPostage,
    isGift,
    giftingDetails,
    personalDetails: base.personalDetails,
    userFormValues: base.userFormValues,
    discount: base.discount,
    discountCode: base.discountCode,
    error: base.error,
    isLoading: base.isLoading,
    isConfirmingFreeBooking: base.isConfirmingFreeBooking,
    isUserFormValid: base.isUserFormValid,

    // Computed
    total,
    discountAmount,
    isFree,
    visibleSteps,
    currentStepIndex,
    isFirstStep,
    isLastStep,
    product,
    postages,

    // Actions
    setQuantity,
    setSelectedPostageId,
    setIsGift,
    updateGiftingDetails,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    updatePersonalDetails: base.updatePersonalDetails,
    updateDeliveryAddress: base.updateDeliveryAddress,
    updateUserFormValue: base.updateUserFormValue,
    setUserFormValues: base.setUserFormValues,
    setIsUserFormValid: base.setIsUserFormValid,
    applyDiscount,
    clearDiscount,
    canContinue,
    getBookingData,
    setIsLoading: base.setIsLoading,
    setIsConfirmingFreeBooking: base.setIsConfirmingFreeBooking,
    setError: base.setError,
    clearError: base.clearError,
  };
}
